import { db } from "../firebase";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import {
  ASSOCIATESTATUS_TYPES,
  FB_COLLECTION_PATH,
  PUSH_NOTIFICATION_TYPES,
} from "./postActionTypes";
import { fx_FB_SendNotification } from "./Fx_SendPushNotification";
import { chatFunction } from "./Fx_Chats";

// ============================================================================
// Invite Codes — Schedulers share a permanent, reusable invite code with
// Werkers. When a Werker redeems the code, both sides are auto-associated
// (mirroring fx_FB_AcceptAssociateRequest) and the Scheduler receives a push
// + in-app notification.
// ============================================================================

const MAX_RETRIES_4DIGIT = 10;
const MAX_RETRIES_5DIGIT = 10;

export const REDEEM_RESULT = {
  Success: "success",
  AlreadyAssociate: "alreadyAssociate",
  SelfCode: "selfCode",
  InvalidCode: "invalidCode",
  Error: "error",
} as const;

export type RedeemResult = (typeof REDEEM_RESULT)[keyof typeof REDEEM_RESULT];

export interface RedeemOutcome {
  result: RedeemResult;
  schedulerFirstName?: string;
  schedulerLastName?: string;
}

function sanitizeInitial(name: string | undefined): string {
  const first = (name ?? "").trim().toUpperCase().match(/[A-Z]/);
  return first ? first[0] : "X";
}

// Test-only export. Exposed for unit testing of the generator's prefix logic.
export const __test__ = { sanitizeInitial };

function randomDigits(count: number): string {
  let out = "";
  for (let i = 0; i < count; i++) {
    out += Math.floor(Math.random() * 10).toString();
  }
  return out;
}

/**
 * Returns the existing invite code for this Scheduler, or generates and
 * atomically claims a new one of the form `{firstInitial}{lastInitial}{####}`.
 * Falls back to 5 digits if all 4-digit retries collide.
 */
export async function fx_FB_GenerateOrGetInviteCode(
  uid: string,
  firstName: string,
  lastName: string
): Promise<string> {
  if (!uid) throw new Error("fx_FB_GenerateOrGetInviteCode: missing uid");

  const userRef = doc(db, FB_COLLECTION_PATH.Users, uid);
  const userSnap = await getDoc(userRef);
  const existing = userSnap.exists() ? (userSnap.data() as any)?.inviteCode : undefined;
  if (typeof existing === "string" && existing.length > 0) {
    return existing;
  }

  const prefix = `${sanitizeInitial(firstName)}${sanitizeInitial(lastName)}`;

  const tryClaim = async (digits: number, attempts: number): Promise<string | null> => {
    for (let i = 0; i < attempts; i++) {
      const code = `${prefix}${randomDigits(digits)}`;
      const codeRef = doc(db, FB_COLLECTION_PATH.InviteCodes, code);
      try {
        const claimed = await runTransaction(db, async (txn) => {
          const snap = await txn.get(codeRef);
          if (snap.exists()) return false;
          txn.set(codeRef, {
            code,
            schedulerUid: uid,
            createdAt: serverTimestamp(),
          });
          return true;
        });
        if (claimed) return code;
      } catch (err) {
        // Transient failure; try another random code.
      }
    }
    return null;
  };

  let code = await tryClaim(4, MAX_RETRIES_4DIGIT);
  if (!code) code = await tryClaim(5, MAX_RETRIES_5DIGIT);
  if (!code) throw new Error("Unable to generate a unique invite code; please try again.");

  await updateDoc(userRef, { inviteCode: code });
  return code;
}

/**
 * Werker-side redemption. Validates the code, then mirrors
 * fx_FB_AcceptAssociateRequest to wire both sides as Associates and notify
 * the Scheduler. Idempotent for already-associated pairs.
 */
export async function fx_FB_RedeemInviteCode(
  werkerUid: string,
  werkerFName: string,
  werkerLName: string,
  werkerCompany: string,
  werkerProfilePicURL: string,
  rawCode: string
): Promise<RedeemOutcome> {
  const code = (rawCode ?? "").trim().toUpperCase();
  if (!code) return { result: REDEEM_RESULT.InvalidCode };

  // Resolve code -> schedulerUid
  const codeSnap = await getDoc(doc(db, FB_COLLECTION_PATH.InviteCodes, code));
  if (!codeSnap.exists()) return { result: REDEEM_RESULT.InvalidCode };
  const schedulerUid = (codeSnap.data() as any)?.schedulerUid as string | undefined;
  if (!schedulerUid) return { result: REDEEM_RESULT.InvalidCode };

  if (schedulerUid === werkerUid) return { result: REDEEM_RESULT.SelfCode };

  // Load scheduler's public-facing profile
  const schedPubSnap = await getDoc(
    doc(db, FB_COLLECTION_PATH.Users, schedulerUid, FB_COLLECTION_PATH.UsersPublicFacing, schedulerUid)
  );
  if (!schedPubSnap.exists()) return { result: REDEEM_RESULT.InvalidCode };
  const sched: any = schedPubSnap.data();
  const schedFName: string = sched.firstName ?? "";
  const schedLName: string = sched.lastName ?? "";
  const schedCompany: string = sched.company ?? "";
  const schedProfilePicURL: string = sched.profilePicURL ?? "../assets/profilePic.png";

  // Already associated? — toast no-op.
  const myAssocRef = doc(db, FB_COLLECTION_PATH.UserAssociations, werkerUid);
  const myAssocSnap = await getDoc(myAssocRef);
  const existingEntry: any = myAssocSnap.exists() ? (myAssocSnap.data() as any)?.[schedulerUid] : undefined;
  if (existingEntry?.associateStatus === ASSOCIATESTATUS_TYPES.Associate) {
    return {
      result: REDEEM_RESULT.AlreadyAssociate,
      schedulerFirstName: schedFName,
      schedulerLastName: schedLName,
    };
  }

  // Mirror fx_FB_AcceptAssociateRequest: write both sides of userAssociations
  await setDoc(
    myAssocRef,
    {
      [schedulerUid]: {
        firstName: schedFName,
        lastName: schedLName,
        company: schedCompany,
        profilePicURL: schedProfilePicURL,
        associateStatus: ASSOCIATESTATUS_TYPES.Associate,
        isRead: true,
        uid: schedulerUid,
      },
    },
    { merge: true }
  );
  await setDoc(
    doc(db, FB_COLLECTION_PATH.UserAssociations, schedulerUid),
    {
      [werkerUid]: {
        firstName: werkerFName,
        lastName: werkerLName,
        company: werkerCompany,
        profilePicURL: werkerProfilePicURL,
        associateStatus: ASSOCIATESTATUS_TYPES.Associate,
        isRead: false,
        uid: werkerUid,
      },
    },
    { merge: true }
  );

  // arrayUnion both users' top-level associate arrays
  await updateDoc(doc(db, FB_COLLECTION_PATH.Users, werkerUid), {
    associates_Associates: arrayUnion(schedulerUid),
  });
  await updateDoc(doc(db, FB_COLLECTION_PATH.Users, schedulerUid), {
    associates_Associates: arrayUnion(werkerUid),
  });

  // Open a DM chat so the pair can communicate immediately (mirrors accept flow)
  try {
    await chatFunction(
      {
        UserId: werkerUid,
        FirstName: werkerFName,
        LastName: werkerLName,
        ProfilePicURL: werkerProfilePicURL,
      },
      {
        WerkerUid: schedulerUid,
        WerkerFirstName: schedFName,
        WerkerLastName: schedLName,
        WerkerProfilePicURL: schedProfilePicURL,
      },
      "DM"
    );
  } catch (err) {
    // Non-fatal: associate link is established even if chat creation fails.
  }

  // Push + in-app notification to Scheduler (deep-links to /AssociateProfile/{werkerUid})
  try {
    await fx_FB_SendNotification(
      werkerUid,
      [schedulerUid],
      PUSH_NOTIFICATION_TYPES.SchedulerNewAssociateFromInvite,
      {
        werkerFName,
        werkerLName,
        werkerUid,
        fromUID: werkerUid,
      }
    );
  } catch (err) {
    // Non-fatal: associate link is established even if notification fails.
  }

  return {
    result: REDEEM_RESULT.Success,
    schedulerFirstName: schedFName,
    schedulerLastName: schedLName,
  };
}
